import React, { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { EXCEPTION_CONFIG, ERROR_SUB_STATUSES, DOT_COLORS } from '../constants/projectStatus';
import type { StatusStep, StatusSubStep } from '../services/projectStatusUtils';

const BADGE_COLORS: Record<string, string> = {
  secondary: '#6c757d',
  info: '#17a2b8',
  primary: '#007bff',
  warning: '#ffc107',
  success: '#28a745',
  danger: '#dc3545'
};

export interface ProjectStatusTimelineProps {
  status: string;
  subStatus?: string | null;
  disputeId?: string | null;
  startDate?: string;
  endDate?: string;
  milestoneCount?: number;
  currentMilestone?: number;
  currentStepSortOrder?: number;
  steps: StatusStep[];
  lang?: 'sv' | 'en';
  onActionClick?: (action: string) => void;
}

type PhaseState = 'done' | 'current' | 'pending';

function getSubStepState(
  subStep: StatusSubStep,
  currentSubStatus: string | null,
  allSubSteps: StatusSubStep[],
  phaseState: PhaseState
): 'done' | 'active' | 'pending' | 'error' {
  if (phaseState === 'done') return 'done';
  if (phaseState === 'pending') return 'pending';
  if (!currentSubStatus) return 'pending';
  if (subStep.key === currentSubStatus && ERROR_SUB_STATUSES.has(subStep.key)) return 'error';
  if (subStep.key === currentSubStatus) return 'active';
  const currentIdx = allSubSteps.findIndex(s => s.key === currentSubStatus);
  const thisIdx = allSubSteps.findIndex(s => s.key === subStep.key);
  if (currentIdx >= 0 && thisIdx < currentIdx) return 'done';
  return 'pending';
}

export default function ProjectStatusTimeline({
  status,
  subStatus = null,
  disputeId,
  milestoneCount,
  currentMilestone,
  currentStepSortOrder,
  steps,
  lang,
  onActionClick
}: ProjectStatusTimelineProps) {
  const { i18n } = useTranslation();
  const currentLang = lang || (i18n.language === 'en' ? 'en' : 'sv');
  const [openStepSortOrder, setOpenStepSortOrder] = useState<number | null>(null);

  // Filter steps for timeline display
  const timelineSteps = steps.filter(s => !s.isSpecial && !s.hideInTimeline);
  const specialSteps = steps.filter(s => s.isSpecial);
  const exceptionConfig = EXCEPTION_CONFIG[status];
  const isException = !!exceptionConfig;
  const exceptionStep = specialSteps.find(s => s.key === status);
  const exceptionColor = exceptionStep?.badgeColor || exceptionConfig?.color || 'warning';
  const exceptionHexColor = BADGE_COLORS[exceptionColor] || BADGE_COLORS.warning;
  const completedStep = specialSteps.find(s => s.key === 'completed');
  const completedHexColor = BADGE_COLORS[completedStep?.badgeColor || 'success'] || BADGE_COLORS.success;
  const conflictCaseStatus = ['under_resolution', 'disputed', 'conflict', 'external_dispute'].includes(status);

  // Find current step — use sortOrder if provided (handles duplicate keys like multiple in_progress).
  // Fall back to status-based lookup when the sortOrder doesn't match a visible step
  // (e.g., when currentStepSortOrder hasn't been advanced past the hidden not_started step).
  const sortOrderIndex = currentStepSortOrder
    ? timelineSteps.findIndex(s => s.sortOrder === currentStepSortOrder)
    : -1;
  const currentStepIndex = sortOrderIndex >= 0
    ? sortOrderIndex
    : timelineSteps.findIndex(s => s.key === status);

  // For repeated keys (milestone, in_progress) introduced by extraStepPattern when
  // there is more than one milestone, suffix the label with its sequence number
  // so each step is clearly identifiable in the timeline.
  const getStepLabel = (step: StatusStep, idx: number): string => {
    const label = currentLang === 'en' ? step.label_en : step.label_sv;
    if (step.key === 'milestone' || step.key === 'in_progress') {
      const sameKeySteps = timelineSteps.filter(s => s.key === step.key);
      if (sameKeySteps.length > 1) {
        const sameKeyIdx = sameKeySteps.indexOf(step);
        if (sameKeyIdx >= 0) return `${label} ${sameKeyIdx + 1}`;
      }
    }
    return label;
  };

  // Build display columns 1:1 with the timeline steps from the project status
  // template. With multiple milestones the template appends an extra
  // (in_progress, milestone) pair per additional milestone — those need to be
  // visible as their own steps so the user sees how many milestones the
  // project has and which one is ongoing right now.
  type DisplayColumn =
    | { type: 'single'; step: StatusStep; idx: number }
    | { type: 'parallel'; members: Array<{ step: StatusStep; idx: number; milestoneNumber: number }> }
    | { type: 'synthetic'; key: string; label_sv: string; label_en: string; badgeColor: string; state: 'done' | 'current' | 'pending' };

  const displayColumns: DisplayColumn[] = timelineSteps.map((step, idx) => ({ type: 'single', step, idx } as DisplayColumn));

  // Append a synthetic "Avslut" column at the end of every project timeline.
  // - done when project status is 'completed'
  // - pending otherwise
  displayColumns.push({
    type: 'synthetic',
    key: 'avslut',
    label_sv: completedStep?.label_sv || 'Avslut',
    label_en: completedStep?.label_en || 'Closing',
    badgeColor: completedStep?.badgeColor || 'success',
    state: status === 'completed' ? 'done' : 'pending'
  });

  // For exception states, use the last normal step as the effective position
  // (the project was somewhere in the flow before the exception)
  const effectiveStepIndex = isException
    ? (currentStepIndex >= 0 ? currentStepIndex : Math.max(0, timelineSteps.length - 2))
    : currentStepIndex;

  // If the project is completed, treat the synthetic Avslut column as current
  // so the active line fills the whole bar.
  const currentColumnIndex = status === 'completed'
    ? displayColumns.length - 1
    : effectiveStepIndex;

  const effectiveProgressPercent = currentColumnIndex >= 0 && displayColumns.length > 1
    ? Math.round((currentColumnIndex / (displayColumns.length - 1)) * 100)
    : 0;

  const memberState = (memberIdx: number): PhaseState => {
    if (status === 'completed') return 'done';
    if (effectiveStepIndex < 0) return 'pending';
    if (memberIdx < effectiveStepIndex) return 'done';
    if (memberIdx === effectiveStepIndex) return 'current';
    return 'pending';
  };

  const columnAggregateState = (col: DisplayColumn): 'done' | 'current' | 'pending' => {
    if (col.type === 'single') return memberState(col.idx);
    if (col.type === 'synthetic') return col.state;
    const states = col.members.map(m => memberState(m.idx));
    if (states.every(s => s === 'done')) return 'done';
    if (states.some(s => s === 'current')) return 'current';
    if (states.some(s => s === 'done')) return 'current';
    return 'pending';
  };

  const colorForState = (state: 'done' | 'current' | 'pending') => {
    if (state === 'done') return '#28a745';
    if (state === 'current') return isException ? exceptionHexColor : '#007bff';
    return '#dee2e6';
  };

  const renderStepDetails = (step: StatusStep, phaseState: PhaseState, compact = false) => {
    if (!step.subSteps?.length) return null;
    const orderedSubSteps = [...step.subSteps].sort((left, right) => left.sortOrder - right.sortOrder);

    return (
      <div className={compact ? 'd-flex flex-column gap-1' : 'd-flex flex-wrap justify-content-center gap-1 mt-2'}>
        {orderedSubSteps.map(subStep => {
          const state = getSubStepState(subStep, subStatus, orderedSubSteps, phaseState);
          return (
            <span
              key={subStep.key}
              className="badge bg-light text-dark border d-inline-flex align-items-center gap-1"
              data-project-status-timeline-detail-step={subStep.key}
              data-state={state}
              style={{
                fontSize: compact ? 11 : 10,
                fontWeight: state === 'active' || state === 'error' ? 700 : 500,
                maxWidth: compact ? 180 : 112,
                whiteSpace: 'normal',
                textAlign: 'left'
              }}
            >
              <span aria-hidden="true" style={getDotStyle(state, subStep.dotColor, 7)} />
              {currentLang === 'en' ? subStep.label_en : subStep.label_sv}
            </span>
          );
        })}
      </div>
    );
  };

  return (
    <div className="project-status-timeline">
      {/* Exception Banner — shown above timeline, not replacing it */}
      {isException && exceptionConfig && (
        <div className={`alert alert-${exceptionColor} py-3 px-3 mb-3`}>
          <div className="d-flex align-items-center gap-2 mb-2">
            <i className={`feather-${exceptionConfig.icon}`} style={{ fontSize: 20 }}></i>
            <div>
              <div className="fw-bold">
                {currentLang === 'en'
                  ? exceptionStep?.label_en || exceptionConfig.label_en
                  : exceptionStep?.label_sv || exceptionConfig.label_sv}
              </div>
              <small>
                {currentLang === 'en' ? exceptionConfig.description_en : exceptionConfig.description_sv}
              </small>
            </div>
          </div>
          {onActionClick && exceptionConfig.actionLabel_sv && (
            <button
              className={`btn btn-sm btn-${exceptionColor}`}
              onClick={() => onActionClick(conflictCaseStatus && disputeId ? `dispute:${disputeId}` : `resume`)}
            >
              <i className={`feather-${conflictCaseStatus ? 'eye' : 'play'} me-1`}></i>
              {currentLang === 'en' ? exceptionConfig.actionLabel_en : exceptionConfig.actionLabel_sv}
            </button>
          )}
        </div>
      )}
      {/* Layer 1: Progress Bar */}
      {/* Desktop: horizontal */}
      <div className="d-none d-md-block">
        <div className="d-flex justify-content-between position-relative mb-2" style={{ alignItems: 'flex-start' }}>
          {/* Background line */}
          <div className="position-absolute" style={{
            top: 16, left: '5%', right: '5%', height: 3,
            backgroundColor: '#e9ecef', zIndex: 0, borderRadius: 2
          }} />
          {/* Active line */}
          <div className="position-absolute" style={{
            top: 16, left: '5%',
            width: `${Math.min(effectiveProgressPercent * 0.9, 90)}%`,
            height: 3, backgroundColor: isException ? exceptionHexColor : (status === 'completed' ? completedHexColor : '#28a745'),
            zIndex: 1, borderRadius: 2, transition: 'width 0.4s ease'
          }} />

          {displayColumns.map((col, colIdx) => {
            if (col.type === 'single') {
              const state = memberState(col.idx);
              const stepColor = colorForState(state);
              const textColor = state === 'done' || state === 'current' ? '#fff' : '#6c757d';
              const hasDetails = !!col.step.subSteps?.length;
              const isExpanded = hasDetails && state === 'current' && !isException;
              const showPopover = hasDetails && openStepSortOrder === col.step.sortOrder && !isExpanded;
              return (
                <div
                  key={`${col.step.key}-${col.step.sortOrder}`}
                  className="d-flex flex-column align-items-center position-relative"
                  data-project-status-timeline-step={col.step.sortOrder}
                  data-state={state}
                  data-label={getStepLabel(col.step, col.idx)}
                  onMouseEnter={() => {
                    if (hasDetails) setOpenStepSortOrder(col.step.sortOrder);
                  }}
                  onMouseLeave={() => setOpenStepSortOrder(current => current === col.step.sortOrder ? null : current)}
                  onFocus={() => {
                    if (hasDetails) setOpenStepSortOrder(col.step.sortOrder);
                  }}
                  onBlur={() => setOpenStepSortOrder(current => current === col.step.sortOrder ? null : current)}
                  style={{ flex: 1, zIndex: showPopover ? 5 : 2, minWidth: 0 }}
                >
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    tabIndex={hasDetails ? 0 : -1}
                    aria-label={getStepLabel(col.step, col.idx)}
                    style={{
                      width: 32, height: 32, backgroundColor: stepColor,
                      color: textColor, fontSize: 13, fontWeight: 600,
                      border: state === 'pending' ? '2px solid #adb5bd' : 'none',
                      transition: 'all 0.3s ease', outline: 'none'
                    }}
                  >
                    {state === 'done' ? (
                      <i className="feather-check" style={{ fontSize: 14 }}></i>
                    ) : (
                      <span>{colIdx + 1}</span>
                    )}
                  </div>
                  <div className="text-center mt-1" style={{ fontSize: 11, lineHeight: 1.2 }}>
                    <span className={state !== 'pending' ? 'fw-semibold' : 'text-muted'}>
                      {getStepLabel(col.step, col.idx)}
                    </span>
                  </div>
                  {isExpanded && renderStepDetails(col.step, state)}
                  {showPopover && (
                    <div
                      className="position-absolute bg-white border rounded shadow-sm p-2"
                      role="tooltip"
                      style={{
                        top: 48,
                        minWidth: 190,
                        maxWidth: 220,
                        zIndex: 10,
                        left: '50%',
                        transform: 'translateX(-50%)'
                      }}
                    >
                      <div className="fw-semibold mb-1" style={{ fontSize: 12 }}>
                        {getStepLabel(col.step, col.idx)}
                      </div>
                      {renderStepDetails(col.step, state, true)}
                    </div>
                  )}
                </div>
              );
            }
            if (col.type === 'synthetic') {
              const state = col.state;
              const stepColor = state === 'done'
                ? BADGE_COLORS[col.badgeColor] || colorForState(state)
                : colorForState(state);
              const textColor = state === 'pending' ? '#6c757d' : '#fff';
              return (
                <div key={col.key} className="d-flex flex-column align-items-center" style={{ flex: 1, zIndex: 2 }}>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle"
                    style={{
                      width: 32, height: 32, backgroundColor: stepColor,
                      color: textColor, fontSize: 13, fontWeight: 600,
                      border: state === 'pending' ? '2px solid #adb5bd' : 'none'
                    }}
                  >
                    {state === 'done'
                      ? <i className="feather-check" style={{ fontSize: 14 }}></i>
                      : <i className="feather-flag" style={{ fontSize: 14 }}></i>}
                  </div>
                  <div className="text-center mt-1" style={{ fontSize: 11, lineHeight: 1.2 }}>
                    <span className={state !== 'pending' ? 'fw-semibold' : 'text-muted'}>
                      {currentLang === 'en' ? col.label_en : col.label_sv}
                    </span>
                  </div>
                </div>
              );
            }
            // Parallel milestone group — vertically stacked branches in one column
            return (
              <div key={`parallel-${colIdx}`} className="d-flex flex-column align-items-center" style={{ flex: 1, zIndex: 2 }}>
                {/* Header circle representing the parallel phase */}
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle"
                  style={{
                    width: 32, height: 32, backgroundColor: colorForState(columnAggregateState(col)),
                    color: columnAggregateState(col) !== 'pending' ? '#fff' : '#6c757d',
                    fontSize: 13, fontWeight: 600,
                    border: columnAggregateState(col) === 'pending' ? '2px solid #adb5bd' : 'none'
                  }}
                >
                  <span>{colIdx + 1}</span>
                </div>
                <div className="text-center mt-1 mb-2" style={{ fontSize: 11, lineHeight: 1.2 }}>
                  <span className="fw-semibold">{currentLang === 'en' ? 'Milestones' : 'Etappmål'}</span>
                  <div className="text-muted" style={{ fontSize: 10 }}>{currentLang === 'en' ? 'parallel' : 'parallellt'}</div>
                </div>
                <div className="d-flex flex-column gap-2" style={{ width: '100%' }}>
                  {col.members.map(m => {
                    const state = memberState(m.idx);
                    const dotColor = colorForState(state);
                    return (
                      <div key={`${m.step.key}-${m.step.sortOrder}`} className="d-flex align-items-center gap-2 px-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          style={{
                            width: 22, height: 22, backgroundColor: dotColor,
                            color: state === 'pending' ? '#6c757d' : '#fff',
                            fontSize: 10, fontWeight: 600,
                            border: state === 'pending' ? '2px solid #adb5bd' : 'none'
                          }}
                        >
                          {state === 'done' ? <i className="feather-check" style={{ fontSize: 10 }}></i> : <span>{m.milestoneNumber}</span>}
                        </div>
                        <span style={{ fontSize: 11 }} className={state !== 'pending' ? 'fw-semibold' : 'text-muted'}>
                          {(currentLang === 'en' ? m.step.label_en : m.step.label_sv)} {m.milestoneNumber}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* Mobile: vertical */}
      <div className="d-md-none">
        {displayColumns.map((col, colIdx) => {
          const isLastCol = colIdx === displayColumns.length - 1;
          if (col.type === 'single') {
            const state = memberState(col.idx);
            const stepColor = colorForState(state);
            const textColor = state === 'pending' ? '#6c757d' : '#fff';
            const isExpanded = !!col.step.subSteps?.length && state === 'current' && !isException;
            return (
              <div
                key={`${col.step.key}-${col.step.sortOrder}-m`}
                className="d-flex align-items-start mb-1"
                data-project-status-timeline-step={col.step.sortOrder}
                data-state={state}
                data-label={getStepLabel(col.step, col.idx)}
              >
                <div className="d-flex flex-column align-items-center me-3" style={{ width: 32 }}>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: 28, height: 28, backgroundColor: stepColor,
                      color: textColor, fontSize: 12, fontWeight: 600,
                      border: state === 'pending' ? '2px solid #adb5bd' : 'none'
                    }}
                  >
                    {state === 'done' ? <i className="feather-check" style={{ fontSize: 12 }}></i> : <span>{colIdx + 1}</span>}
                  </div>
                  {!isLastCol && (
                    <div style={{
                      width: 2,
                      height: isExpanded ? Math.max(52, (col.step.subSteps?.length || 0) * 22) : 20,
                      backgroundColor: state === 'done' ? '#28a745' : '#e9ecef'
                    }} />
                  )}
                </div>
                <div className="pt-1 flex-grow-1" style={{ fontSize: 13 }}>
                  <span className={state !== 'pending' ? 'fw-semibold' : 'text-muted'}>
                    {getStepLabel(col.step, col.idx)}
                  </span>
                  {isExpanded && renderStepDetails(col.step, state)}
                </div>
              </div>
            );
          }
          if (col.type === 'synthetic') {
            const state = col.state;
            const stepColor = state === 'done'
              ? BADGE_COLORS[col.badgeColor] || colorForState(state)
              : colorForState(state);
            const textColor = state === 'pending' ? '#6c757d' : '#fff';
            return (
              <div key={col.key} className="d-flex align-items-start mb-1">
                <div className="d-flex flex-column align-items-center me-3" style={{ width: 32 }}>
                  <div
                    className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                    style={{
                      width: 28, height: 28, backgroundColor: stepColor,
                      color: textColor, fontSize: 12, fontWeight: 600,
                      border: state === 'pending' ? '2px solid #adb5bd' : 'none'
                    }}
                  >
                    {state === 'done'
                      ? <i className="feather-check" style={{ fontSize: 12 }}></i>
                      : <i className="feather-flag" style={{ fontSize: 12 }}></i>}
                  </div>
                </div>
                <div className="pt-1" style={{ fontSize: 13 }}>
                  <span className={state !== 'pending' ? 'fw-semibold' : 'text-muted'}>
                    {currentLang === 'en' ? col.label_en : col.label_sv}
                  </span>
                </div>
              </div>
            );
          }
          // Parallel milestone group (mobile)
          const aggState = columnAggregateState(col);
          const aggColor = colorForState(aggState);
          return (
            <div key={`parallel-m-${colIdx}`} className="d-flex align-items-start mb-1">
              <div className="d-flex flex-column align-items-center me-3" style={{ width: 32 }}>
                <div
                  className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                  style={{
                    width: 28, height: 28, backgroundColor: aggColor,
                    color: aggState === 'pending' ? '#6c757d' : '#fff',
                    fontSize: 12, fontWeight: 600,
                    border: aggState === 'pending' ? '2px solid #adb5bd' : 'none'
                  }}
                >
                  <span>{colIdx + 1}</span>
                </div>
                {!isLastCol && (
                  <div style={{ width: 2, height: 20, backgroundColor: aggState === 'done' ? '#28a745' : '#e9ecef' }} />
                )}
              </div>
              <div className="pt-1 flex-grow-1" style={{ fontSize: 13 }}>
                <span className="fw-semibold">{currentLang === 'en' ? 'Milestones' : 'Etappmål'}</span>
                <span className="text-muted ms-1" style={{ fontSize: 11 }}>({currentLang === 'en' ? 'parallel' : 'parallellt'})</span>
                <div className="d-flex flex-column gap-1 mt-1">
                  {col.members.map(m => {
                    const mState = memberState(m.idx);
                    return (
                      <div key={`${m.step.key}-${m.step.sortOrder}-m`} className="d-flex align-items-center gap-2">
                        <div
                          className="d-flex align-items-center justify-content-center rounded-circle flex-shrink-0"
                          style={{
                            width: 20, height: 20, backgroundColor: colorForState(mState),
                            color: mState === 'pending' ? '#6c757d' : '#fff',
                            fontSize: 10, fontWeight: 600,
                            border: mState === 'pending' ? '2px solid #adb5bd' : 'none'
                          }}
                        >
                          {mState === 'done' ? <i className="feather-check" style={{ fontSize: 10 }}></i> : <span>{m.milestoneNumber}</span>}
                        </div>
                        <span style={{ fontSize: 12 }} className={mState !== 'pending' ? 'fw-semibold' : 'text-muted'}>
                          {(currentLang === 'en' ? m.step.label_en : m.step.label_sv)} {m.milestoneNumber}
                        </span>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          );
        })}
      </div>

      {/* A project may be parked here. Conflict is started only from a disputed
          milestone without a time-based escalation lock. */}
      {!isException && status !== 'completed' && status !== 'not_started' && onActionClick && (
        <div className="d-flex gap-2 mt-3">
          <button
            className="btn btn-sm btn-outline-warning"
            onClick={() => onActionClick('set_on_hold')}
          >
            <i className="feather-pause-circle me-1"></i>
            {currentLang === 'en' ? 'Mark as On Hold' : 'Markera som Vilande'}
          </button>
        </div>
      )}
    </div>
  );
}

function getDotStyle(state: string, dotColor: string, size: number): React.CSSProperties {
  const configuredColor = DOT_COLORS[dotColor] || DOT_COLORS.grey;
  switch (state) {
    case 'done':
      return {
        width: size, height: size, borderRadius: '50%',
        backgroundColor: DOT_COLORS.green.bg, flexShrink: 0
      };
    case 'active':
      return {
        width: size, height: size, borderRadius: '50%',
        backgroundColor: DOT_COLORS.blue.bg, flexShrink: 0,
        boxShadow: `0 0 0 3px rgba(0,123,255,0.25)`
      };
    case 'error':
      return {
        width: size, height: size, borderRadius: '50%',
        backgroundColor: DOT_COLORS.red.bg, flexShrink: 0
      };
    default:
      return {
        width: size, height: size, borderRadius: '50%',
        backgroundColor: configuredColor.bg, border: `1px solid ${configuredColor.border}`,
        flexShrink: 0
      };
  }
}
